import { useEffect, useMemo, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { enhancedAuthService } from "@/services/admin/enhanced-auth.service";
import { PermissionChecker, type UserRole } from "@/types/roles";
import type { AgentContext, AgentScope, AgentCapabilities } from "../types/agent";
import { useCurrentOrganization } from "@/hooks/useCurrentOrganization";
import { useCurrentProject } from "@/hooks/useCurrentProject";

function detectScope(pathname: string): AgentScope {
  if (pathname.startsWith("/admin")) {return "admin";}
  if (pathname.startsWith("/profile")) {return "profile";}
  if (pathname.startsWith("/community")) {return "community";}
  if (pathname.startsWith("/tasks") || pathname.startsWith("/admin/tasks")) {return "tasks";}
  if (pathname === "/" || pathname.startsWith("/home")) {return "site";}
  return "unknown";
}

export function useAgentCapabilities(): AgentContext {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { currentOrganization } = useCurrentOrganization();
  const { currentProject } = useCurrentProject();
  const [roles, setRoles] = useState<string[]>([]);
  const [primaryRole, setPrimaryRole] = useState<UserRole | undefined>(undefined);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const ctx = await enhancedAuthService.getSecurityContext();
        if (!mounted) {return;}
        const roleStrings = ctx.roles || [];
        setRoles(roleStrings);
        // Determine primary app role by choosing the highest privilege known role present
        const knownRoles: UserRole[] = ["architect", "admin", "moderator", "user", "viewer"];
        const found = knownRoles.find(r => roleStrings.includes(r));
        setPrimaryRole(found);
      } catch (_e) {
        // Fall back to undefined -> viewer
        setRoles([]);
        setPrimaryRole(undefined);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const scope = detectScope(pathname);

  const capabilities: AgentCapabilities = useMemo(() => {
    const cfg = PermissionChecker.getRoleConfig(primaryRole);
    return {
      canSeeAdminData: PermissionChecker.hasElevatedPrivileges(primaryRole),
      canPerformAdminActions: PermissionChecker.isAdmin(primaryRole) || PermissionChecker.isArchitect(primaryRole),
      canManageUsers: cfg.permissions.admin.canManageUsers,
      canViewAuditLogs: cfg.permissions.admin.canViewAuditLogs,
      canCreateTodos: cfg.permissions.tasks.canCreate,
      canEditTodos: cfg.permissions.tasks.canEditAll || cfg.permissions.tasks.canEditOwn,
      canExportData: cfg.permissions.features.exportData,
      canUseAdvancedSearch: cfg.permissions.features.advancedSearch,
      braindumpDailyLimit: PermissionChecker.getBraindumpLimits(primaryRole).dailyLimit,
      braindumpMonthlyLimit: PermissionChecker.getBraindumpLimits(primaryRole).monthlyLimit,
    };
  }, [primaryRole]);

  // Compute domain expertise based on current route
  const expertise = useMemo(() => {
    const p = pathname;
    
    // Sales Portal & CRM (check before generic /admin)
    if (p.includes('/admin/sales-portal') || p.includes('/sales')) {return { domain: 'sales-portal' as const, description: 'Expert in sales pipeline, deals, commissions, and team performance', suggestedTools: ['useSalesPortalQueries', 'SalesPortal', 'CommissionManager'] };}
    if (p.includes('/admin/clients') || p.includes('/clients')) {return { domain: 'clients' as const, description: 'Expert in client management, CRM workflows, and relationship tracking', suggestedTools: ['useClients', 'ClientService'] };}
    if (p.includes('/admin/opportunities') || p.includes('/opportunities')) {return { domain: 'opportunities' as const, description: 'Expert in opportunity tracking, deal stages, and pipeline management', suggestedTools: ['useOpportunities', 'OpportunityService'] };}
    
    // Tasks & Productivity
    if (p.includes('/admin/tasks') || p.includes('/tasks')) {return { domain: 'tasks' as const, description: 'Expert in task workflows, filters, subtasks, and priority management', suggestedTools: ['useTodoOperations', 'useTodoFilters', 'ProfessionalTodoList'] };}
    if (p.includes('/admin/time-management')) {return { domain: 'time-management' as const, description: 'Expert in time blocks, productivity tracking, and scheduling optimization', suggestedTools: ['useTimeBlocks', 'TimeBlockService'] };}
    if (p.includes('/admin/workflows')) {return { domain: 'workflows' as const, description: 'Expert in building and optimizing automations and workflows', suggestedTools: ['smartAssignmentService', 'assignment.service'] };}
    
    // Calendar
    if (p.includes('/admin/calendar') || p.includes('/calendar')) {return { domain: 'calendar' as const, description: 'Expert in scheduling, availability, and Google Calendar diagnostics', suggestedTools: ['AdminCalendarDashboard', 'GoogleCalendarTestPanel'] };}
    
    // Team & Users
    if (p.includes('/admin/users')) {return { domain: 'users' as const, description: 'Expert in user management, roles, and permissions', suggestedTools: ['UsersController', 'EnhancedAuthService'] };}
    if (p.includes('/admin/team') || p.includes('/team')) {return { domain: 'team' as const, description: 'Expert in team hierarchy, territories, and organizational structure', suggestedTools: ['useTeamHierarchy', 'TeamHierarchyService'] };}
    
    // Gamification & Engagement
    if (p.includes('/admin/leaderboards') || p.includes('/leaderboards')) {return { domain: 'leaderboards' as const, description: 'Expert in leaderboard rankings, competitive metrics, and team standings', suggestedTools: ['useGamification', 'LeaderboardService'] };}
    if (p.includes('/admin/achievements') || p.includes('/achievements')) {return { domain: 'achievements' as const, description: 'Expert in achievements, badges, XP systems, and milestone tracking', suggestedTools: ['useGamification', 'AchievementService'] };}
    if (p.includes('/admin/seasons') || p.includes('/seasons')) {return { domain: 'seasons' as const, description: 'Expert in seasonal competitions, challenges, and time-limited events', suggestedTools: ['useSeason', 'SeasonService'] };}
    if (p.includes('/admin/streaks') || p.includes('/streaks')) {return { domain: 'gamification' as const, description: 'Expert in streak tracking, habit building, and consistency rewards', suggestedTools: ['useGamification', 'StreakManager'] };}
    
    // Community
    if (p.includes('/community')) {return { domain: 'community' as const, description: 'Expert in community engagement, posts, discussions, and social features', suggestedTools: ['postsService', 'commentsService', 'likesService'] };}
    
    // Analytics & Reports
    if (p.includes('/admin/progress-reports') || p.includes('/progress')) {return { domain: 'progress-reports' as const, description: 'Expert in progress tracking, performance reports, and productivity analytics', suggestedTools: ['useProgressReports', 'ProgressReportService'] };}
    if (p.includes('/admin/audit') || p.includes('/admin/logs')) {return { domain: 'audit' as const, description: 'Expert in audit logs, system change history, and compliance tracking', suggestedTools: ['AuditLogs', 'AuditService'] };}
    
    // Financial
    if (p.includes('/admin/subscriptions')) {return { domain: 'subscriptions' as const, description: 'Expert in subscription analytics, renewals, and cost tracking', suggestedTools: ['useSubscriptions', 'SubscriptionOverview'] };}
    if (p.includes('/admin/accounting')) {return { domain: 'accounting' as const, description: 'Expert in financial tracking, transactions, and accounting workflows', suggestedTools: ['AccountingOverview', 'useAccounting'] };}
    
    // Content & Docs
    if (p.includes('/admin/docs') || p.includes('/docs')) {return { domain: 'docs' as const, description: 'Expert in internal documentation and developer guides', suggestedTools: ['DocumentViewer'] };}
    if (p.includes('/admin/content')) {return { domain: 'content' as const, description: 'Expert in content management and digital assets', suggestedTools: ['ContentManagement'] };}
    
    // Storage
    if (p.includes('/admin/attachments')) {return { domain: 'attachments' as const, description: 'Expert in file management, storage, and archival policies', suggestedTools: ['AttachmentService'] };}
    
    // Profile
    if (p.includes('/profile')) {return { domain: 'general' as const, description: 'Expert in user profile settings, preferences, and personalization' };}
    
    // Admin Dashboard (fallback for /admin routes)
    if (p.startsWith('/admin')) {return { domain: 'dashboard' as const, description: 'Expert in admin overview, system navigation, and platform management' };}
    
    // General fallback
    return { domain: 'general' as const, description: 'Always-on AI teammate ready to help with any task' };
  }, [pathname]);

  const agentContext: AgentContext = useMemo(() => ({
    role: primaryRole,
    roles,
    scope,
    capabilities,
    expertise,
    organizationId: currentOrganization?.id,
    organizationName: currentOrganization?.name,
    currentRoute: pathname,
    selectedProjectId: currentProject?.id,
  }), [primaryRole, roles, scope, capabilities, expertise, currentOrganization, currentProject, pathname]);

  return agentContext;
}
